import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '$lib/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const base =
  'inline-flex items-center justify-center gap-1.5 rounded-sm border font-medium ' +
  'select-none transition-colors duration-fast disabled:opacity-50 disabled:pointer-events-none';

const variants: Record<Variant, string> = {
  primary:
    'bg-accent text-accent-fg border-accent hover:bg-accent/90 active:bg-accent/85',
  secondary:
    'bg-surface text-fg border-rule-strong hover:bg-surface-2 active:bg-surface-3',
  ghost:
    'bg-transparent text-fg border-transparent hover:bg-surface-2 active:bg-surface-3',
  danger:
    'bg-danger text-white border-danger hover:bg-danger/90 active:bg-danger/85'
};

const sizes: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-xs',
  md: 'h-8 px-3 text-sm'
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', type = 'button', ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(base, variants[variant], sizes[size], className)}
      {...rest}
    />
  );
});
